import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, EMPTY, first, map, type Observable, of, switchMap, timeout } from 'rxjs';

import {
  type CanvasElementId,
  findByRefIds,
  WrapperFilter,
  type WrapperRefs,
} from '@blaze-runtime/engine-domain';
import { type PropertiesConfigInputListFiltersModel } from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { BlazeSchema, type DatasetColumn, findColumn } from '@blaze-shared/schema-domain';
import { isDefined } from '@blaze-shared/shared-util';

import { ElementsFacade, updateElementProps } from '../../+state/elements';
import { getWrapperRefs } from '../../canvas-elements/element-model/wrapper-refs';
import { ElementDefinitionsService } from '../../services/element-definitions.service';
import { ElementTransformationService } from '../../services/element-transformation.service';
import { SearchAndFiltersWrapperService } from '../../services/search-and-filters-wrapper.service';
import {
  type ContainerFieldFilter,
  type ContainerFilterType,
  toContainerFilterElementConfig,
} from '../models';

import { resolveFilterControlType } from './resolve-filter-control-type';

const FIELD_FILTERS_REFRESH_TIMEOUT_MS = 10_000;

const CONTAINER_FILTER_HOSTS = {
  table: { label: 'Table element', filterFieldKey: (c: DatasetColumn) => c.id ?? c.name },
  list: { label: 'List element', filterFieldKey: (c: DatasetColumn) => c.name },
} as const;

export type ContainerFieldFilterElementType = keyof typeof CONTAINER_FILTER_HOSTS;

export interface ApplyContainerFieldFiltersOptions {
  elementId: CanvasElementId;
  elementType: ContainerFieldFilterElementType;
  fieldFilters: ContainerFieldFilter[] | undefined;
  datasetId: string | undefined;
}

interface PreparedFieldFilter {
  model: PropertiesConfigInputListFiltersModel;
  filterType: ContainerFilterType | undefined;
}

@Injectable({ providedIn: 'root' })
export class ContainerFieldFiltersApplierService {
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly elementsFacade = inject(ElementsFacade);
  private readonly elementTransformationService = inject(ElementTransformationService);
  private readonly searchAndFiltersWrapperService = inject(SearchAndFiltersWrapperService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);

  apply({
    elementId,
    elementType,
    fieldFilters,
    datasetId,
  }: ApplyContainerFieldFiltersOptions): Observable<void> {
    if (!fieldFilters?.length) {
      return of(undefined);
    }
    const host = CONTAINER_FILTER_HOSTS[elementType];
    const warnPrefix = `${host.label} (id:${elementId})`;
    const columns$: Observable<BlazeSchema['columns'] | undefined> = datasetId
      ? this.schemaService.get(datasetId).pipe(
          first(Boolean),
          map((schema) => schema?.columns),
        )
      : of(undefined);

    return columns$.pipe(
      switchMap((columns) =>
        this.writeFieldFilters(elementId, fieldFilters, columns, host.filterFieldKey, warnPrefix),
      ),
    );
  }

  private writeFieldFilters(
    elementId: CanvasElementId,
    fieldFilters: ContainerFieldFilter[],
    columns: DatasetColumn[] | undefined,
    filterFieldKey: (column: DatasetColumn) => string,
    warnPrefix: string,
  ): Observable<void> {
    const prepared = fieldFilters
      .map((filter, id) => this.prepareFieldFilter(id, filter, columns, filterFieldKey, warnPrefix))
      .filter((filter): filter is PreparedFieldFilter => !!filter);

    if (!prepared.length) {
      return of(undefined);
    }

    this.store.dispatch(
      updateElementProps({
        elementId,
        path: 'content.searchAndFilters.filters',
        value: prepared.map((filter) => filter.model),
      }),
    );

    return this.waitForFilterWrapperRefs(
      elementId,
      prepared.map((filter) => filter.model.id).filter(isDefined),
      warnPrefix,
    ).pipe(
      map(() => {
        this.applyFilterControlTypes(elementId, prepared, warnPrefix);
      }),
    );
  }

  private prepareFieldFilter(
    id: number,
    filter: ContainerFieldFilter,
    columns: DatasetColumn[] | undefined,
    filterFieldKey: (column: DatasetColumn) => string,
    warnPrefix: string,
  ): PreparedFieldFilter | undefined {
    const column = findColumn(columns, filter.field);
    if (!column) {
      console.warn(
        `${warnPrefix}: fieldFilters field "${filter.field}" could not be resolved to a column - filter skipped`,
      );
      return undefined;
    }

    const config = filter.filterType
      ? toContainerFilterElementConfig(filter.filterType)
      : undefined;

    return {
      model: {
        id,
        placeholder: filter.label,
        filterField: filterFieldKey(column),
        operator: config?.operator,
        filterOperatorType: config?.filterOperatorType,
        predefinedOperators: config?.predefinedOperators,
      },
      filterType: filter.filterType,
    };
  }

  private applyFilterControlTypes(
    elementId: CanvasElementId,
    prepared: PreparedFieldFilter[],
    warnPrefix: string,
  ): void {
    prepared.forEach(({ model, filterType }) => {
      if (!filterType) {
        return;
      }

      const config = toContainerFilterElementConfig(filterType);
      const observed = this.searchAndFiltersWrapperService.getFilterControlType(
        elementId,
        model.id,
      );
      if (!observed?.id || !observed.type) {
        return;
      }

      const observedType = observed.type;
      const targetType = config.elementType;
      const resolved = resolveFilterControlType({
        observedType,
        targetType,
        canTransform: () =>
          this.elementTransformationService.canTransform(observedType, targetType),
        isFilterCapable: () =>
          this.elementDefinitionsService.getFeature(targetType, 'searchAndFiltersFieldSupport') ===
          true,
      });

      if (resolved.targetType) {
        this.searchAndFiltersWrapperService.updateFilterElementType(
          elementId,
          model.id,
          resolved.targetType,
        );
      }

      if (resolved.isFallback) {
        console.warn(
          `${warnPrefix}: filter "${model.placeholder}" could not use ${config.elementType} for the observed ${observed.type} control`,
        );
        return;
      }

      if (config.contentMode === 'chip') {
        this.store.dispatch(
          updateElementProps({
            elementId: observed.id,
            path: 'content.mode',
            value: config.contentMode,
          }),
        );
      }
    });
  }

  private waitForFilterWrapperRefs(
    containerElementId: CanvasElementId,
    filterIds: number[],
    warnPrefix: string,
  ): Observable<void> {
    return this.elementsFacade.getElement(containerElementId).pipe(
      map((element) => hasRefForEveryFilter(getWrapperRefs(element), filterIds)),
      first(Boolean),
      timeout({
        first: FIELD_FILTERS_REFRESH_TIMEOUT_MS,
        with: () => {
          console.warn(`${warnPrefix}: field filters did not materialize in time`);
          return EMPTY;
        },
      }),
      catchError((error) => {
        console.warn(`${warnPrefix}: field filters wait failed`, error);
        return EMPTY;
      }),
      map((): void => undefined),
    );
  }
}

function hasRefForEveryFilter(refs: WrapperRefs, filterIds: number[]): boolean {
  return filterIds.every(
    (filterId) => findByRefIds(refs, WrapperFilter.getRefIds(String(filterId))) !== undefined,
  );
}
